import { ReactNode } from "react";

import {
  type BulkAction,
  type BulkActionHandlersByType,
  type BulkActionsQuery,
  BulkActionsProvider as SharedBulkActionsProvider,
} from "@punto/frontend-shared/components/bulk-actions";

import { $api } from "@/lib/api";
import { routePaths } from "@/router/route-paths";

/**
 * Per-bulk-action-type detail route for an incident's `referenceId`. Passed to
 * the shared panel; unmapped types render their label as plain text.
 */
export const DETAIL_ROUTE_BY_TYPE: Record<string, string> = {
  bulk_regenerate_tra050: routePaths.energySavingCertificate,
  bulk_change_certificate_status: routePaths.energySavingCertificate,
};

const BULK_ACTIONS_PATH = "/v1/bulk-actions";

/**
 * The poll the shared provider drives, declared at module scope as a real
 * `use*` hook rather than built inside the component: the shared provider calls
 * it unconditionally on every render, so hook order stays stable, and the
 * reference is stable without a `useCallback` wrapper.
 */
const useBulkActionsQuery: BulkActionsQuery = ({ enabled, refetchInterval }) =>
  $api.useQuery(
    "get",
    BULK_ACTIONS_PATH,
    {},
    {
      enabled,
      refetchInterval: ({ state }) =>
        refetchInterval(state.data as BulkAction[] | undefined),
    },
  ) as { data: BulkAction[] | undefined };

/**
 * Janus's binding of the shared bulk-actions provider: it owns the endpoint and
 * the `$api` client; the shared package owns the tracking and polling policy.
 * The per-type settle/download handlers come in from the app shell
 * (`useBulkActionHandlers`), so this module never imports the features that
 * import it; without them, actions are tracked and polled but nothing runs on
 * settle.
 */
export const BulkActionsProvider = ({
  children,
  handlersByType,
}: {
  children: ReactNode;
  handlersByType?: BulkActionHandlersByType;
}) => (
  <SharedBulkActionsProvider
    useActionsQuery={useBulkActionsQuery}
    actionsQueryPath={BULK_ACTIONS_PATH}
    handlersByType={handlersByType}
  >
    {children}
  </SharedBulkActionsProvider>
);
